'use client';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logAdServed from '@alltrails/amplitude/events/logAdServed';
import logRemoveAdClicked from '@alltrails/amplitude/events/logRemoveAdClicked';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import useIntersectionObserverCallback from '@alltrails/core/hooks/useIntersectionObserverCallback';
import useToggle from '@alltrails/core/hooks/useToggle';
import logError from '@alltrails/core/utils/logError';
import Button from '@alltrails/denali/components/Button';
import type Experiments from '@alltrails/types/Experiments';
import type Trail from '@alltrails/types/Trail';
import styles from './Ad.module.scss';

const threshold = Array.from({ length: 101 }, (_, i) => i / 100);

type Props = {
  adUnitPath: string;
  buttonClassName?: string;
  className?: string;
  adElementClassName?: string;
  experiment?: NonNullable<Experiments['web-growth-native-ads-v2']>['value'];
  id: string;
  adSize?: [number, number][];
  screenSize?: 'desktop' | 'mobile';
  trail?: Trail;
  onDisplay?: () => void;
  lazyLoad?: boolean;
  showGoAdFreeButton?: boolean;
};

// Desktop: https://www.figma.com/file/3dCQyKmNlOdfjaZ8vsDpzs/Native-Ads?node-id=464%3A140041&t=6m6YpR4rfbKIw3Ft-4
// Mobile: https://www.figma.com/file/3dCQyKmNlOdfjaZ8vsDpzs/Native-Ads?node-id=464%3A140300&t=6m6YpR4rfbKIw3Ft-4
// ad experiment: https://www.figma.com/design/mGFBM8T5WalA5d44YLGV1I/Ads---sponsorships?node-id=320-52350&m=dev
export default function Ad({
  adUnitPath,
  buttonClassName,
  className,
  adElementClassName,
  experiment,
  id,
  adSize = [[300, 250]],
  screenSize,
  trail,
  onDisplay,
  lazyLoad = false,
  showGoAdFreeButton = true
}: Props) {
  const user = useUser();
  const adRef = useRef<HTMLDivElement>(null);
  const [loadAd, setLoadAd] = useState(!user?.featureEntitlements?.includes('ad_free') && !lazyLoad);
  const [displayAd, toggleDisplayAd] = useToggle(false);

  // Supposedly there is a way to enable lazy loading with googletag.pubads().enableLazyLoad
  // but it doesn't seem to work - ads load immediately on page load
  // https://support.google.com/admanager/thread/258572019/enablelazyload-not-working-with-gpt-ads
  // So, doing an intersection observer to lazy load the ad on our end instead
  const adObserver = useIntersectionObserverCallback(
    ([entry]) => {
      if (!user?.featureEntitlements?.includes('ad_free') && entry.isIntersecting && lazyLoad && !loadAd) {
        setLoadAd(true);
      }
    },
    { threshold }
  );

  useEffect(() => {
    const adSection = adRef.current;

    if (adObserver && adSection) {
      adObserver.observe(adSection);
    }

    return () => {
      if (adObserver) {
        adObserver.disconnect();
      }
    };
  }, [adObserver]);

  useEffect(() => {
    const existing = document.getElementById('gpt');

    if (!existing) {
      const script = document.createElement('script');
      script.id = 'gpt';
      script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
      script.async = true;
      document.body.appendChild(script);
    }

    window.googletag = window.googletag || { cmd: [] };

    if (!window.googletag.cmd) {
      logError(new Error('window.googletag.cmd is undefined'), event => {
        event.addMetadata('googletag', window.googletag);
      });
    }
  }, []);

  useEffect(() => {
    const handleSlotRenderEnded = event => {
      if (event.slot.getSlotElementId() === id && !event.isEmpty) {
        logAdServed({ adunitpath: adUnitPath, advertiser_id: event.advertiserId, location: 'TrailDetails' });
        toggleDisplayAd();
        onDisplay?.();
      }
    };

    // could do a <FreeAd component that wraps <Ad to avoid loading scripts on plus pages
    if (loadAd && window.googletag?.cmd) {
      googletag.cmd.push(() => {
        let slot = googletag.defineSlot(adUnitPath, adSize, id);
        if (slot) {
          slot = slot.addService(googletag.pubads());

          if (process.env.NEXT_PUBLIC_DEPLOY_NAMESPACE !== 'production') {
            slot.setTargeting('test', '1');
          }

          // trail can be null if you view a lists page and scroll down to reviews i.e. <TrailUgcWrapper and it would also be confusing since each
          // review is a different trail
          if (trail) {
            slot.setTargeting('activity', trail.attributes?.activities.map(activity => activity.uid) ?? []);
            slot.setTargeting('feature', trail.attributes?.features.map(feature => feature.uid) ?? []);
            slot.setTargeting('is_nps', trail.location?.country === 'US' && trail.area?.type === 'N' ? '1' : '0');
          }

          if (experiment === 'unpaid_only') {
            slot.setTargeting('2023adtest', 'unpaid');
          }

          if (experiment === 'unpaid-and-paid') {
            slot.setTargeting('2023adtest', ['paid', 'unpaid']);
          }

          if (screenSize === 'desktop') {
            const mapping = googletag.sizeMapping().addSize([768, 0], adSize).addSize([0, 0], []).build();
            slot.defineSizeMapping(mapping);
          }

          if (screenSize === 'mobile') {
            const mapping = googletag.sizeMapping().addSize([768, 0], []).addSize([0, 0], adSize).build();
            slot.defineSizeMapping(mapping);
          }
        }

        googletag.enableServices();

        googletag.display(id);

        googletag.pubads().addEventListener('slotRenderEnded', handleSlotRenderEnded);
      });
    }

    return () => {
      if (loadAd && window.googletag?.cmd) {
        googletag.cmd.push(() => {
          googletag.pubads().removeEventListener('slotRenderEnded', handleSlotRenderEnded);

          const slots = googletag
            .pubads()
            .getSlots()
            .filter(slot => slot.getAdUnitPath() === adUnitPath);

          googletag.destroySlots(slots);
        });
      }
    };
  }, [adUnitPath, experiment, id, onDisplay, screenSize, toggleDisplayAd, loadAd]);

  return (
    <>
      <div ref={adRef} />
      <div className={classNames(styles.ad, className)} style={{ display: !displayAd ? 'none' : undefined }}>
        <Typography className={styles.advertisement} color="secondary" variant="text100">
          <FormattedMessage defaultMessage="This ad supports outdoor access for all" />
        </Typography>
        <div id={id} className={adElementClassName} />
        {showGoAdFreeButton && (
          <Button
            className={classNames(styles.button, buttonClassName)}
            linkInfo={{ href: '/plans' }}
            onClick={() => {
              logRemoveAdClicked({ location: 'TrailDetails' });
            }}
            text={<FormattedMessage defaultMessage="Remove ads" />}
          />
        )}
      </div>
    </>
  );
}
